import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";

const RegistrarAsistencia = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [nombreActividad, setNombreActividad] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [fecha, setFecha] = useState("");
  const [matriculaId, setMatriculaId] = useState("");

  const [aprendices, setAprendices] = useState([]);
  const [asistencias, setAsistencias] = useState({});
  const [tieneAsistencias, setTieneAsistencias] = useState(false);

  const [tarjetaUID, setTarjetaUID] = useState("");
  const uidInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const disabledAll = useMemo(() => loading || saving, [loading, saving]);

  // ==================== cargar actividad ====================
  useEffect(() => {
    const obtenerActividad = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/actividad/listid/${id}`);
        const datos = res.data;

        setNombreActividad(datos.nombreActividad || "");
        setDescripcion(datos.descripcion || "");
        setFecha(datos.fecha ? datos.fecha.substring(0, 10) : "");
        setMatriculaId(datos.matricula || "");
      } catch (error) {
        console.error("Error al obtener la actividad:", error);
        Swal.fire("Error", "No se pudo cargar la actividad.", "error");
      } finally {
        setLoading(false);
      }
    };

    obtenerActividad();
  }, [id]);

  // ==================== cargar aprendices por matrícula ====================
  useEffect(() => {
    const obtenerAprendices = async () => {
      try {
        if (!matriculaId) return;

        setLoading(true);
        const res = await axios.get(`/api/detallematricula/listbyid/${matriculaId}`);
        const datos = res.data;

        const detalles = datos?.detalles || [];
        setAprendices(detalles);

        const asistenciasInicial = {};
        detalles.forEach((aprendiz) => {
          asistenciasInicial[aprendiz._id] = {
            asistio: false,
            justificacion: "",
          };
        });
        setAsistencias(asistenciasInicial);
      } catch (error) {
        console.error("Error al obtener aprendices:", error);
        Swal.fire("Error", "No se pudo cargar la lista de aprendices.", "error");
      } finally {
        setLoading(false);
      }
    };

    obtenerAprendices();
  }, [matriculaId]);

  // ==================== cargar asistencias existentes ====================
  useEffect(() => {
    const obtenerAsistencias = async () => {
      try {
        const res = await axios.get(`/api/asistencia/actividad/${id}`);
        const asistenciasExistentes = res.data;

        if (Array.isArray(asistenciasExistentes) && asistenciasExistentes.length > 0) {
          const nuevoEstado = {};
          asistenciasExistentes.forEach((asistencia) => {
            nuevoEstado[asistencia.detalleMatricula] = {
              id: asistencia._id,
              asistio: asistencia.asistio,
              justificacion: asistencia.justificacion || "",
            };
          });

          setAsistencias((prev) => ({ ...prev, ...nuevoEstado }));
          setTieneAsistencias(true);
        } else {
          setTieneAsistencias(false);
        }
      } catch (error) {
        console.warn("No se pudieron cargar asistencias previas:", error.message);
        setTieneAsistencias(false);
      }
    };

    if (aprendices.length > 0) obtenerAsistencias();
  }, [aprendices, id]);

  // ==================== handlers ====================
  const handleCheckboxChange = (idAprendiz) => {
    if (tieneAsistencias) return; // bloqueado si ya existían (según tu lógica)
    setAsistencias((prev) => ({
      ...prev,
      [idAprendiz]: {
        ...prev[idAprendiz],
        asistio: !prev[idAprendiz]?.asistio,
      },
    }));
  };

  const handleJustificacionChange = (idAprendiz, value) => {
    setAsistencias((prev) => ({
      ...prev,
      [idAprendiz]: {
        ...prev[idAprendiz],
        justificacion: value,
      },
    }));
  };

  // ==================== ESCANEO UID ====================
  const handleUIDScan = async (e) => {
    if (e.key === "Enter" && tarjetaUID.trim() !== "") {
      e.preventDefault();

      try {
        const res = await axios.post("/api/tarjetas/buscar-detalle-matricula", {
          tarjetaUID: tarjetaUID.trim(),
        });

        const { detalleMatriculaId, nombreCompleto } = res.data;

        setAsistencias((prev) => ({
          ...prev,
          [detalleMatriculaId]: {
            ...(prev[detalleMatriculaId] || {}),
            asistio: true,
          },
        }));

        Swal.fire({
          icon: "success",
          title: "✅ Asistencia registrada",
          html: `<p class="mb-1"><strong>${nombreCompleto || "Aprendiz"}</strong></p>
                 <p class="mb-0">PRESENTE EN LA FORMACIÓN</p>`,
          timer: 2000,
          showConfirmButton: false,
        });

        setTarjetaUID("");
        uidInputRef.current?.focus();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Tarjeta no reconocida",
          text: error.response?.data?.message || "No se pudo procesar la tarjeta",
        });

        setTarjetaUID("");
        uidInputRef.current?.focus();
      }
    }
  };

  // ==================== Guardar / actualizar ====================
  const saveAsistencia = async () => {
    try {
      setSaving(true);

      const registros = aprendices.map((aprendiz) => {
        const datos = asistencias[aprendiz._id] || {};
        return {
          actividad: id,
          detalleMatricula: aprendiz._id,
          asistio: datos.asistio || false,
          justificacion: datos.justificacion || "",
        };
      });

      await axios.post("/api/asistencia/addmany", registros, {
        headers: { "Content-Type": "application/json" },
      });

      await Swal.fire({
        icon: "success",
        title: "Asistencias registradas",
        text: "✅ Las asistencias se guardaron correctamente.",
        timer: 1600,
        showConfirmButton: false,
      });

      navigate(-1);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text:
          error.response?.data?.error ||
          error.message ||
          "Ocurrió un error al registrar las asistencias.",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateAsistencia = async () => {
    try {
      setSaving(true);

      const registros = aprendices.map((aprendiz) => {
        const datos = asistencias[aprendiz._id] || {};
        return {
          id: datos.id,
          justificacion: datos.justificacion || "",
        };
      });

      const response = await axios.put("/api/asistencia/updatemany", registros, {
        headers: { "Content-Type": "application/json" },
      });

      await Swal.fire({
        icon: "success",
        title: "Asistencias actualizadas",
        text: `${response.data.actualizados || registros.length} registros actualizados correctamente.`,
        timer: 1600,
        showConfirmButton: false,
      });

      navigate(-1);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text:
          error.response?.data?.error ||
          error.message ||
          "Ocurrió un error al actualizar las asistencias.",
      });
    } finally {
      setSaving(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    tieneAsistencias ? await updateAsistencia() : await saveAsistencia();
  };

  // ==================== UI helpers ====================
  const totalPresentes = useMemo(() => {
    return Object.values(asistencias || {}).filter((x) => x?.asistio).length;
  }, [asistencias]);

  const totalAprendices = aprendices?.length || 0;

  useEffect(() => {
    // foco al input cuando ya está la vista lista
    if (!disabledAll) uidInputRef.current?.focus();
  }, [disabledAll]);

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* HEADER */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                  <h5 className="mt-2 mb-1 fw-semibold">
                    {tieneAsistencias ? "Editar Asistencia" : "Registrar Asistencia"}
                  </h5>
                <small className="text-secondary">
                  Escanea la tarjeta o marca manualmente. Agrega observaciones si aplica.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={disabledAll}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => uidInputRef.current?.focus()}
                  disabled={disabledAll}
                  title="Enfocar lector UID"
                >
                  <i className="fa-solid fa-qrcode me-2"></i>
                  Escanear
                </button>
              </div>
            </div>
          </div>

          {/* BODY */}
          <div className="p-3 p-md-4">
            {(loading || saving) && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" aria-hidden="true" />
                <div className="mt-2">{loading ? "Cargando…" : "Guardando…"}</div>
              </div>
            )}

            <form onSubmit={actions}>
              <div className="row g-3">
                {/* PANEL IZQUIERDO */}
                <div className="col-12 col-md-4">
                  <div className="border rounded-4 p-3 p-md-4 bg-body-tertiary">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <div className="fw-semibold">Actividad</div>
                        <div className="text-secondary small">
                          {totalPresentes}/{totalAprendices} presentes
                        </div>
                      </div>

                      <div className="text-end">
                        <span className="badge text-bg-dark">
                          <i className="fa-solid fa-calendar-day me-1"></i>
                          {fecha || "—"}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Nombre</label>
                      <input
                        type="text"
                        className="form-control"
                        value={nombreActividad}
                        readOnly
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Descripción</label>
                      <textarea className="form-control" value={descripcion} rows={3} readOnly />
                    </div>

                    {/* SCANNER */}
                    <div className="border rounded-4 bg-white p-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="fw-semibold">
                          <i className="fa-solid fa-id-card me-2 text-secondary"></i>
                          Escanear Tarjeta UID
                        </div>
                        <span className="badge text-bg-secondary">Enter</span>
                      </div>

                      <input
                        ref={uidInputRef}
                        type="text"
                        className="form-control"
                        placeholder="Escanee la tarjeta del aprendiz"
                        value={tarjetaUID}
                        onChange={(e) => setTarjetaUID(e.target.value)}
                        onKeyDown={handleUIDScan}
                        disabled={disabledAll}
                      />

                      <div className="form-text">
                        Escanea y se marcará como <strong>Presente</strong> automáticamente.
                      </div>
                    </div>

                    {/* BOTONES */}
                    <div className="d-grid mt-3 gap-2">
                      <button
                        type="submit"
                        className={`btn ${tieneAsistencias ? "btn-outline-secondary" : "btn btn-dark"}`}
                        disabled={disabledAll}
                        title={tieneAsistencias ? "Actualizar asistencias" : "Guardar asistencias"}
                      >
                        <i
                          className={`fa-solid ${saving ? "fa-spinner fa-spin" : tieneAsistencias ? "fa-rotate" : "fa-floppy-disk"
                            } me-2`}
                        ></i>
                        {tieneAsistencias ? "Actualizar" : "Guardar"}
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => navigate(-1)}
                        disabled={disabledAll}
                        title="Cancelar"
                      >
                        <i className="fa-solid fa-xmark me-2"></i>
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>

                {/* PANEL DERECHO */}
                <div className="col-12 col-md-8">
                  {/* TABLA (md+) */}
                  <div className="d-none d-md-block">
                    <div className="border rounded-4 overflow-hidden">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th>Aprendiz</th>
                            <th className="text-center" style={{ width: 130 }}>
                              Asistencia
                            </th>
                            <th>Observaciones</th>
                          </tr>
                        </thead>

                        <tbody>
                          {aprendices.length > 0 ? (
                            aprendices.map((item, index) => {
                              const estado = asistencias[item._id]?.asistio;
                              return (
                                <tr key={index} className={estado ? "table-success" : ""}>
                                  <td className="fw-semibold">{item.nombreAprendiz}</td>

                                  <td className="text-center">
                                    <div className="form-check d-inline-flex align-items-center justify-content-center gap-2">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={!!estado}
                                        onChange={() => handleCheckboxChange(item._id)}
                                        disabled={disabledAll || tieneAsistencias}
                                      />
                                      <span className={`badge ${estado ? "text-bg-success" : "text-bg-secondary"}`}>
                                        {estado ? "Presente" : "Ausente"}
                                      </span>
                                    </div>
                                  </td>

                                  <td>
                                    <input
                                      className="form-control"
                                      value={asistencias[item._id]?.justificacion || ""}
                                      onChange={(e) => handleJustificacionChange(item._id, e.target.value)}
                                      placeholder="Observación / justificación…"
                                      disabled={disabledAll}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center text-secondary py-4">
                                <i className="fa-solid fa-circle-info me-2"></i>
                                No hay aprendices registrados
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* CARDS (sm) */}
                  <div className="d-md-none">
                    {aprendices.length > 0 ? (
                      aprendices.map((item, index) => {
                        const estado = asistencias[item._id]?.asistio;
                        return (
                          <div
                            key={index}
                            className={`card mb-3 shadow-sm border-0 ${estado ? "border border-success" : ""}`}
                          >
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start">
                                <h6 className="mb-1 fw-semibold">{item.nombreAprendiz}</h6>
                                <span className={`badge ${estado ? "text-bg-success" : "text-bg-secondary"}`}>
                                  {estado ? "Presente" : "Ausente"}
                                </span>
                              </div>

                              <div className="mt-2">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={!!estado}
                                    onChange={() => handleCheckboxChange(item._id)}
                                    disabled={disabledAll || tieneAsistencias}
                                    id={`asistencia-${item._id}`}
                                  />
                                  <label className="form-check-label" htmlFor={`asistencia-${item._id}`}>
                                    Marcar asistencia
                                  </label>
                                </div>
                              </div>

                              <div className="mt-2">
                                <label className="form-label small text-secondary mb-1">Observaciones</label>
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={asistencias[item._id]?.justificacion || ""}
                                  onChange={(e) => handleJustificacionChange(item._id, e.target.value)}
                                  placeholder="Observación / justificación…"
                                  disabled={disabledAll}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="alert alert-info text-center mb-0">
                        <i className="fa-solid fa-circle-info me-2"></i>
                        No hay aprendices registrados
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
          {/* /BODY */}
        </div>
      </div>
    </div>
  );
};

export default RegistrarAsistencia;
